import { Loader2, Wand2 } from "lucide-react";
import { ChipGroup, Field, inputCls } from "../components/Chips";
import { SongRow } from "../components/SongRow";
import { CoverForm } from "../components/CoverForm";
import { YueBridgeCard } from "../components/YueBridgeCard";
import { CustomFields, ModelPickers, SimpleFields, SongOptions } from "../components/CreateFields";
import { useCreateForm } from "../hooks/useCreateForm";
import { useMySongs } from "../hooks/useMySongs";
import { http } from "../lib/api";
import { trashSong } from "../lib/trash";
import { GENRES, LENGTHS, MOODS, VOICES } from "../lib/presets";

const ModeTabs = ({ mode, setMode }) => (
  <div className="inline-flex p-1 rounded-full bg-[var(--c-surface)] border border-[var(--c-line)]">
    {[["simple", "Paprastas"], ["custom", "Individualus"], ["cover", "Cover"]].map(([id, label]) => (
      <button key={id} data-testid={`mode-tab-${id}`} onClick={() => setMode(id)}
        className={`px-5 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${mode === id ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}>
        {label}
      </button>
    ))}
  </div>
);

const EmptyQueue = () => (
  <div className="text-center py-16 px-6">
    <div className="flex justify-center gap-1 h-10 items-end mb-5">{[0, 0.12, 0.24, 0.36, 0.48, 0.6, 0.72].map((d) => <span key={`eq-${d}`} className="eq-bar w-1.5 h-full rounded bg-zinc-700" style={{ animationDelay: `${d}s` }} />)}</div>
    <p className="text-sm text-zinc-400">Čia atsiras tavo dainos.</p>
  </div>
);

const MySongsPanel = ({ songs, reload }) => {
  const done = songs.filter((s) => s.status === "completed");
  const del = (s) => trashSong(s, reload);
  const like = async (s) => { await http.post(`/songs/${s.id}/like`); reload(); };
  return (
    <section className="lg:sticky lg:top-8 self-start rise" style={{ animationDelay: "120ms" }}>
      <div className="rounded-3xl border border-[var(--c-line)] bg-[var(--c-surface)]/60 p-5">
        <div className="flex items-center justify-between px-2 mb-4">
          <h2 className="font-display text-base md:text-lg">Mano kūriniai</h2>
          <span data-testid="my-songs-count" className="text-xs font-mono text-zinc-500">{songs.length}</span>
        </div>
        <div data-testid="generation-queue" className="space-y-1 max-h-[calc(100vh-14rem)] overflow-auto scroll-thin pr-1">
          {songs.length === 0 && <EmptyQueue />}
          {songs.map((s) => <SongRow key={s.id} song={s} list={done} onLike={like} onDelete={del} />)}
        </div>
      </div>
    </section>
  );
};

const SubmitButton = ({ busy, onClick }) => (
  <button data-testid="create-song-submit-button" onClick={onClick} disabled={busy}
    className="w-full sm:w-auto flex items-center justify-center gap-2.5 px-10 py-4 rounded-full bg-[var(--c-orange)] text-white font-display font-semibold glow-orange hover:bg-[#ff7a26] active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-60">
    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}Kurti dainą
  </button>
);

const SongForm = ({ form }) => {
  const { mode, f, setF, set, eng, instr, comparing, busy, submit } = form;
  return (
    <>
      {mode === "simple" ? <SimpleFields f={f} set={set} /> : <CustomFields f={f} set={set} instr={instr} />}
      <ChipGroup label="Žanras" items={GENRES} value={f.genre} onChange={set("genre")} prefix="genre-chip" />
      <ChipGroup label="Nuotaika" items={MOODS} value={f.moods} onChange={set("moods")} prefix="mood-chip" color="violet" multi />
      {!instr && <ChipGroup label="Vokalas" items={VOICES} value={f.voice} onChange={set("voice")} prefix="voice-chip" color="violet" />}
      {mode === "custom" && (
        <Field label="Papildomas stilius (angliškai tiksliau)">
          <input data-testid="song-style-input" value={f.extra} onChange={(e) => set("extra")(e.target.value)} className={inputCls}
            placeholder="pvz.: 120 bpm, saxophone solo, vintage reverb" />
        </Field>
      )}
      <ChipGroup label="Dainos ilgis" items={LENGTHS} value={f.length} onChange={(v) => set("length")(v || "medium")} prefix="length-chip" />
      <div className="space-y-6">
        <ModelPickers f={f} setF={setF} instr={instr} />
        {f.engine === "yue_local" && <YueBridgeCard />}
        <Field label="Autorius">
          <input data-testid="author-name-input" maxLength={40} value={f.author} onChange={(e) => set("author")(e.target.value)}
            className={inputCls} placeholder="Jūsų vardas ar slapyvardis" />
        </Field>
      </div>
      <SongOptions f={f} set={set} eng={eng} instr={instr} comparing={comparing} />
      <SubmitButton busy={busy} onClick={submit} />
    </>
  );
};

export default function Create() {
  const { songs, load } = useMySongs();
  const form = useCreateForm(load);

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] gap-10 xl:gap-16">
      <section className="rise">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--c-orange)] font-semibold mb-4">Studija</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">Sukurk dainą<br /><span className="text-zinc-500">lietuviškai.</span></h1>
        <p className="text-base text-zinc-400 mt-5 max-w-lg">Aprašyk idėją arba parašyk savo žodžius — gausi pilną dainą su vokalu, viršeliu ir tekstu.</p>
        <div className="mt-10 space-y-8">
          <ModeTabs mode={form.mode} setMode={form.setMode} />
          {form.mode === "cover" ? <CoverForm sourceId={form.coverId} onCreated={load} /> : <SongForm form={form} />}
        </div>
      </section>
      <MySongsPanel songs={songs} reload={load} />
    </div>
  );
}
